import React, { useEffect, useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Collapse,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setLanguage, setFont } from "../../Redux/Settings/action";
import ProfileModal from "../Profile/ProfileModal";
import { userLogoutActionFn } from "../../Redux/Auth/action";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import MenuIcon from "@mui/icons-material/Menu";
import PieChartIcon from "@mui/icons-material/PieChart";
import HomeIcon from "@mui/icons-material/Home";
import BusinessIcon from "@mui/icons-material/Business";
import ApartmentIcon from "@mui/icons-material/Apartment";
import WorkIcon from "@mui/icons-material/Work";
import LibraryAddCheckIcon from "@mui/icons-material/LibraryAddCheck";
import CreateIcon from "@mui/icons-material/Create";
import LogoutIcon from "@mui/icons-material/Logout";
import EmailIcon from "@mui/icons-material/Email";
import { motion, AnimatePresence } from "framer-motion";
import longLogo from "../../assets/digital-sherpa-logo.svg";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import BedtimeIcon from "@mui/icons-material/Bedtime";
import BedtimeOffIcon from "@mui/icons-material/BedtimeOff";
import TuneIcon from "@mui/icons-material/Tune";

const ProfileTopBar = ({ toggleThemeMode, themeMode }) => {
  const { pathname } = useLocation();
  const [userData, setUserData] = useState(null);
  const [updateProfile, setUpdateProfile] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const dispatch = useDispatch();
  const [openRoles, setOpenRoles] = useState(false);
  const [bedTime, setBedTime] = useState(0);
  const { language, font } = useSelector((store) => store.settingsReducer);
  const handleClick = () => setOpenDrawer(true);
  const handleClose = () => setOpenDrawer(false);
  const handleCloseUpdateProfile = () => setUpdateProfile(false);

  const LANGUAGES = ["English", "Bengali", "Hindi", "Tamil"];
  const FONTS = ["Roboto", "Poppins", "Inter", "Montserrat"];

  const handleLanguageChange = (lang) => {
    dispatch(setLanguage(lang));
    setOpenFilter(false);
  };


  const handleFontChange = (f) => {
    dispatch(setFont(f));
    setOpenFilter(false);
  };

  // Close filter dropdown when clicking outside
  useEffect(() => {
    if (!openFilter) return;
    const handleOutsideClick = () => setOpenFilter(false);
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [openFilter]);

  useEffect(() => {
    const userD = JSON.parse(window.localStorage.getItem("userLogin"));
    if (userD) setUserData(userD.data);
  }, []);

  // Disable background scroll when drawer is open
  useEffect(() => {
    if (openDrawer) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [openDrawer]);

  const handleLogoutClick = () => {
    dispatch(userLogoutActionFn());
  };

  const handleBrightness = () => {
    toggleThemeMode();
    setBedTime((bedTime === 1 ? 0 : 1));
  };
  return (
    <Box
      sx={{
        height: "fit-content",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        boxSizing: "border-box",
        paddingRight: "0px",
        marginBottom: "15px",
        position: "sticky",
        gap: "10px",
        top: 0,
        zIndex: 1200,
      }}
    >
      {/* Filter By */}
      <Box
        className="notranslate"
        translate="no"
        sx={{ position: "relative" }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box
          onClick={() => setOpenFilter((prev) => !prev)}
          id="filter-by-button"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            cursor: "pointer",
            border: "1px solid gray",
            borderRadius: "50px",
            px: 1.5,
            py: 0.6,
            fontSize: "13px",
            fontWeight: 500,
            userSelect: "none",
            transition: "background 0.2s",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <TuneIcon sx={{ fontSize: 20, mr: 0.5 }} />
          <Typography variant="body2" fontWeight={1000} fontSize={13}>
            Filter By
          </Typography>
          <ExpandMore
            sx={{
              fontSize: 18,
              transform: openFilter ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          />
        </Box>

        {/* First-level dropdown: Language | Font */}
        <AnimatePresence>
          {openFilter && (
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.15 }}
              sx={{
                position: "absolute",
                top: "calc(100% + 6px)",
                right: 0,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "10px",
                boxShadow: "0 8px 28px rgba(0,0,0,0.13)",
                minWidth: 160,
                zIndex: 1400,
                py: 0.5,
              }}
            >
              {/* Language row */}
              <Box
                sx={{ position: "relative" }}
                onMouseEnter={() => setOpenFilter("language")}
                onMouseLeave={() => setOpenFilter(true)}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2, py: 1,
                    cursor: "default",
                    borderRadius: "6px",
                    mx: 0.5,
                    bgcolor: openFilter === "language" ? "action.hover" : "transparent",
                    transition: "background 0.15s",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Typography fontSize={13} fontWeight={500}>Language</Typography>
                  <ExpandMore sx={{ fontSize: 16, transform: "rotate(-90deg)", ml: 2, color: "text.secondary" }} />
                </Box>

                <AnimatePresence>
                  {openFilter === "language" && (
                    <Box
                      component={motion.div}
                      initial={{ opacity: 0, x: 6 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 6 }}
                      transition={{ duration: 0.13 }}
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: "calc(100% + 6px)",
                        bgcolor: "background.paper",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "10px",
                        boxShadow: "0 8px 28px rgba(0,0,0,0.13)",
                        minWidth: 150,
                        py: 0.5,
                        zIndex: 1500,
                      }}
                    >
                      {LANGUAGES.map((lang) => (
                        <Box
                          key={lang}
                          id={`lang-option-${lang}`}
                          onClick={() => handleLanguageChange(lang)}
                          sx={{
                            px: 2, py: 0.9, mx: 0.5,
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "13px",
                            fontWeight: language === lang ? 600 : 400,
                            bgcolor: language === lang ? "primary.main" : "transparent",
                            color: language === lang ? "white" : "text.primary",
                            transition: "background 0.13s",
                            "&:hover": { bgcolor: language === lang ? "primary.main" : "action.hover" },
                          }}
                        >
                          {lang}
                        </Box>
                      ))}
                    </Box>
                  )}
                </AnimatePresence>
              </Box>

              {/* Divider */}
              <Box sx={{ my: 0.5, mx: 1, borderBottom: "1px solid", borderColor: "divider" }} />

              {/* Font row */}
              <Box
                sx={{ position: "relative" }}
                onMouseEnter={() => setOpenFilter("font")}
                onMouseLeave={() => setOpenFilter(true)}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2, py: 1,
                    cursor: "default",
                    borderRadius: "6px",
                    mx: 0.5,
                    bgcolor: openFilter === "font" ? "action.hover" : "transparent",
                    transition: "background 0.15s",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Typography fontSize={13} fontWeight={500}>Font</Typography>
                  <ExpandMore sx={{ fontSize: 16, transform: "rotate(-90deg)", ml: 2, color: "text.secondary" }} />
                </Box>

                <AnimatePresence>
                  {openFilter === "font" && (
                    <Box
                      component={motion.div}
                      initial={{ opacity: 0, x: 6 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 6 }}
                      transition={{ duration: 0.13 }}
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: "calc(100% + 6px)",
                        bgcolor: "background.paper",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "10px",
                        boxShadow: "0 8px 28px rgba(0,0,0,0.13)",
                        minWidth: 150,
                        py: 0.5,
                        zIndex: 1500,
                      }}
                    >
                      {FONTS.map((f) => (
                        <Box
                          key={f}
                          id={`font-option-${f}`}
                          onClick={() => handleFontChange(f)}
                          sx={{
                            px: 2, py: 0.9, mx: 0.5,
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "13px",
                            fontFamily: `'${f}', sans-serif`,
                            fontWeight: font === f ? 600 : 400,
                            bgcolor: font === f ? "primary.main" : "transparent",
                            color: font === f ? "white" : "text.primary",
                            transition: "background 0.13s",
                            "&:hover": { bgcolor: font === f ? "primary.main" : "action.hover" },
                          }}
                        >
                          {f}
                        </Box>
                      ))}
                    </Box>
                  )}
                </AnimatePresence>
              </Box>
            </Box>
          )}
        </AnimatePresence>
      </Box>





      {/* Theme toggle */}
      <Tooltip
        title={
          themeMode === "light" ? "Switch to Dark mode" : "Switch to Light mode"
        }
        arrow
        placement="left"
      >
        <IconButton onClick={handleBrightness} size="small">
          {/* {bedTime === 0 ? <BedtimeIcon /> : <BedtimeOffIcon />} */}
          {themeMode === "light" ? <BedtimeIcon /> : <BedtimeOffIcon />}
        </IconButton>
      </Tooltip>

      {/* Profile Menu Button */}
      <Box
        onClick={handleClick}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "5px",
          cursor: "pointer",
          border: "1px solid gray",
          borderRadius: "50px",
          padding: "3px",
        }}
      >
        <AccountCircleIcon style={{ fontSize: 30 }} />
        <MenuIcon style={{ fontSize: 30 }} />
      </Box>

      {/* Drawer + Overlay */}
      <AnimatePresence>
        {openDrawer && (
          <>
            {/* Overlay */}
            <Box
              component={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={handleClose} // clicking outside closes drawer
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                bgcolor: "black",
                zIndex: 1200,
              }}
            />

            {/* Drawer */}
            <Box
              component={motion.div}
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 100, damping: 18 }}
              sx={{
                backgroundColor: "primary.main",
                color: "white",
                position: "fixed",
                top: 0,
                right: 0,
                height: "95vh",
                width: { xs: "85%", sm: "360px" },
                boxShadow: "-4px 0px 20px rgba(0,0,0,0.25)",
                zIndex: 1301, // higher than overlay
                borderTopLeftRadius: "5px",
                borderBottomLeftRadius: "5px",
                overflowY: "auto",
                p: 2.5,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Drawer Header */}

              {/* Logo */}
              <ArrowForwardIcon
                onClick={handleClose}
                sx={{ color: "white", marginBottom: "16px" }}
              />
              <Box
                sx={{
                  width: "100%",
                  height: "60px",
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={longLogo}
                  alt="Digital Sherpa"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    backgroundColor: "white",
                    borderRadius: "10px",
                    padding: "6px",
                  }}
                />
              </Box>

              <Divider sx={{ mb: 1.5 }} />

              {/* User Info */}
              <Box sx={{ mb: 2 }}>
                <ListItemButton sx={{ pl: 0, pr: 0 }}>
                  <ListItemIcon sx={{ minWidth: "32px", mr: "-6px" }}>
                    <AccountCircleIcon color="white" sx={{ color: "white" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={userData?.fullname || "User Name"}
                    primaryTypographyProps={{
                      sx: { fontWeight: 500, fontSize: "15px" },
                    }}
                  />
                </ListItemButton>
                <ListItemButton sx={{ pl: 0, pr: 0 }}>
                  <ListItemIcon sx={{ minWidth: "32px", mr: "-6px" }}>
                    <EmailIcon color="white" sx={{ color: "white" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={userData?.email}
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: 500,
                        fontSize: "15px",
                        wordBreak: "break-all",
                      },
                    }}
                  />
                </ListItemButton>
              </Box>

              {/* Roles Collapsible */}
              <Box sx={{ mb: 2 }}>
                <ListItemButton
                  onClick={() => setOpenRoles(!openRoles)}
                  sx={{
                    px: 0,
                    borderRadius: "8px",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        variant="subtitle2"
                        sx={{ color: "white", fontWeight: 500 }}
                      >
                        Roles
                      </Typography>
                    }
                  />
                  {openRoles ? (
                    <ExpandLess sx={{ color: "white" }} />
                  ) : (
                    <ExpandMore sx={{ color: "white" }} />
                  )}
                </ListItemButton>

                <Collapse in={openRoles} timeout="auto" unmountOnExit>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                      mt: 1,
                    }}
                  >
                    {userData?.roles?.length > 0 ? (
                      userData.roles.map((role, idx) => (
                        <Chip
                          key={idx}
                          label={role}
                          sx={{
                            color: "white",
                            border: "1px solid white",
                            background: "transparent",
                            fontSize: "13px",
                          }}
                          size="small"
                        />
                      ))
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ color: "text.disabled" }}
                      >
                        No roles assigned.
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Box
                  sx={{
                    display: {
                      xs: "block",
                      sm: "block",
                      md: "none",
                      lg: "none",
                    },
                  }}
                >
                  {[
                    {
                      to: "/questions",
                      icon: <PieChartIcon />,
                      label: "Questions",
                    },
                    {
                      to: "/openings_table",
                      icon: <HomeIcon />,
                      label: "Home",
                    },
                    {
                      to: "/hiring_company",
                      icon: <BusinessIcon />,
                      label: "Hiring Company",
                    },
                    {
                      to: "/recruiter_tab_dashboard",
                      icon: <ApartmentIcon />,
                      label: "Recruitment Company",
                    },
                    {
                      to: "/job_role_table",
                      icon: <WorkIcon />,
                      label: "Job Role",
                    },
                    {
                      to: "/selected_question",
                      icon: <LibraryAddCheckIcon />,
                      label: "Selected Questions",
                    },
                  ].map((item, index) => (
                    <Link
                      key={index}
                      to={item.to}
                      style={{ textDecoration: "none", color: "white" }}
                    >
                      <ListItemButton
                        sx={{
                          pl: 0,
                          borderRadius: "8px",
                          mb: 0.5,
                          transition: "0.3s",
                          "&:hover": { backgroundColor: "action.hover" },
                        }}
                        onClick={handleClose}
                      >
                        <ListItemIcon sx={{ minWidth: "35px", color: "white" }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            sx: { fontSize: "14px", fontWeight: 500 },
                          }}
                        />
                      </ListItemButton>
                    </Link>
                  ))}
                </Box>
                <Box></Box>
                <ListItemButton
                  sx={{
                    pl: 0,
                    borderRadius: "8px",
                    mb: 0.5,
                    transition: "0.3s",
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                  onClick={() => setUpdateProfile(true)}
                >
                  <ListItemIcon sx={{ minWidth: "35px" }}>
                    <CreateIcon color="primary" sx={{ color: "white" }} />
                  </ListItemIcon>
                  <ListItemText primary="Update Profile" />
                </ListItemButton>

                <ListItemButton
                  sx={{
                    pl: 0,
                    borderRadius: "8px",
                    transition: "0.3s",
                    color: "error.main",
                    "&:hover": {
                      backgroundColor: "rgba(255, 0, 0, 0.05)",
                    },
                  }}
                  onClick={handleLogoutClick}
                >
                  <ListItemIcon>
                    <LogoutIcon color="error" />
                  </ListItemIcon>
                  <ListItemText primary="Logout" />
                </ListItemButton>
              </Box>
            </Box>
          </>
        )}
      </AnimatePresence>

      {/* Profile Modal */}
      <ProfileModal
        open={updateProfile}
        handleClose={handleCloseUpdateProfile}
      />
    </Box>
  );
};

export default ProfileTopBar;
